import React from 'react';
import { useParams } from 'react-router-dom';
import ShopLayout from '../../components/shop/ShopLayout';

// Policy content configurations
const policies = {
  'privacy': {
    title: 'Privacy Policy',
    lastUpdated: 'January 2024',
    sections: [
      {
        title: 'Information We Collect',
        content: `We collect information you provide directly to us, such as when you create an account, make a purchase, contact us, or participate in any interactive features. This information may include:
        
• Name, email address, postal address, phone number
• Payment information (processed securely through payment partners)
• Device information and usage data
• Location data (with your consent)
• Communication preferences`
      },
      {
        title: 'How We Use Your Information',
        content: `We use the information we collect to:
        
• Process and fulfill your orders
• Send you transaction confirmations and updates
• Respond to your comments and questions
• Provide customer service
• Send marketing communications (with your consent)
• Improve our services and develop new features
• Detect and prevent fraud`
      },
      {
        title: 'Information Sharing',
        content: `We do not sell, trade, or rent your personal information to third parties. We may share your information with:
        
• Service providers who assist in our operations
• Payment processors for transaction processing
• Shipping partners for order delivery
• Legal authorities when required by law`
      },
      {
        title: 'Data Security',
        content: `We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. All payment transactions are encrypted using SSL technology.`
      },
      {
        title: 'Your Rights',
        content: `You have the right to:
        
• Access your personal information
• Correct inaccurate data
• Request deletion of your data
• Opt-out of marketing communications
• Export your data in a portable format`
      },
      {
        title: 'Contact Us',
        content: `If you have questions about this Privacy Policy, please contact us at:
        
Email: privacy@zobox.in
Phone: +91 98765 43210
Address: ZOBOX Protech Limited, Gurgaon, Haryana, India`
      }
    ]
  },
  'terms': {
    title: 'Terms & Conditions',
    lastUpdated: 'January 2024',
    sections: [
      {
        title: 'Acceptance of Terms',
        content: `By accessing and using the ZOBOX website and services, you accept and agree to be bound by these Terms and Conditions. If you do not agree to these terms, please do not use our services.`
      },
      {
        title: 'Products and Services',
        content: `ZOBOX offers refurbished smartphones, laptops, and related accessories. All products are graded based on their condition:
        
• ZOPRO (Excellent, Superb, Good) - 6 months warranty
• ZRP (Fair, Okay) - 7 days checking warranty

Product images are for illustration purposes. Actual product appearance may vary slightly.`
      },
      {
        title: 'Pricing and Payment',
        content: `• All prices are in Indian Rupees (INR) and include applicable taxes
• Prices are subject to change without notice
• We accept multiple payment methods including UPI, Credit/Debit cards, Net Banking
• Orders are confirmed only after successful payment`
      },
      {
        title: 'Shipping and Delivery',
        content: `• Free delivery on orders above ₹999
• Delivery within 5-7 business days across India
• Delivery partners: Delhivery, BlueDart, DTDC
• You will receive tracking information via SMS and email`
      },
      {
        title: 'Returns and Refunds',
        content: `• 7-day return policy for all products
• Products must be in original condition with all accessories
• Refunds processed within 5-7 business days
• Please refer to our Return Policy for detailed information`
      },
      {
        title: 'Warranty',
        content: `• ZOPRO products: 6 months warranty against manufacturing defects
• ZRP products: 7 days checking warranty
• Warranty does not cover physical damage, water damage, or unauthorized repairs
• Please refer to our Warranty Policy for detailed information`
      },
      {
        title: 'User Accounts',
        content: `• You are responsible for maintaining the confidentiality of your account
• You must provide accurate and complete information
• You are responsible for all activities under your account
• We reserve the right to terminate accounts that violate these terms`
      },
      {
        title: 'Limitation of Liability',
        content: `ZOBOX shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our services or products.`
      },
      {
        title: 'Governing Law',
        content: `These Terms shall be governed by and construed in accordance with the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Gurgaon, Haryana.`
      }
    ]
  },
  'return-policy': {
    title: 'Return Policy',
    lastUpdated: 'January 2024',
    sections: [
      {
        title: 'Return Window',
        content: `We offer a 7-day return policy from the date of delivery. If you're not satisfied with your purchase, you can initiate a return within this period.`
      },
      {
        title: 'Eligibility for Returns',
        content: `To be eligible for a return:
        
• Product must be in original condition
• All original accessories, box, and packaging must be included
• Product should not have any physical damage caused by the user
• IMEI/Serial number must match the invoice`
      },
      {
        title: 'Non-Returnable Items',
        content: `The following cannot be returned:
        
• Products with physical damage or water damage
• Products missing original accessories
• Products with mismatched IMEI/Serial number
• Products returned after 7 days
• Accessories (screen guards, cases, etc.)`
      },
      {
        title: 'How to Initiate a Return',
        content: `1. Login to your ZOBOX account
2. Go to 'My Orders' section
3. Select the order and click 'Return'
4. Choose return reason and submit
5. Our team will arrange pickup within 2-3 business days`
      },
      {
        title: 'Refund Process',
        content: `• Refunds are processed after quality check of returned product
• Refund timeline: 5-7 business days after product reaches our warehouse
• Refunds are credited to the original payment method
• For COD orders, refunds are processed via bank transfer`
      },
      {
        title: 'Exchange Policy',
        content: `If you received a defective product or wrong item, we offer free exchange:
        
• Contact us within 24 hours of delivery
• Share unboxing video as proof
• We'll arrange pickup and send replacement`
      }
    ]
  },
  'warranty-policy': {
    title: 'Warranty Policy',
    lastUpdated: 'January 2024',
    sections: [
      {
        title: 'Warranty Coverage',
        content: `ZOBOX provides warranty coverage on all refurbished devices:
        
• ZOPRO Products (Excellent, Superb, Good grades): 6 months warranty
• ZRP Products (Fair, Okay grades): 7 days checking warranty`
      },
      {
        title: 'What\'s Covered',
        content: `Our warranty covers:
        
• Manufacturing defects
• Hardware malfunctions (motherboard, charging port, buttons)
• Display issues (not caused by damage)
• Battery issues (capacity below 80% health)
• Speaker and microphone failures`
      },
      {
        title: 'What\'s Not Covered',
        content: `Warranty does not cover:
        
• Physical damage (cracks, dents, scratches)
• Water or liquid damage
• Damage from unauthorized repairs
• Software issues or user-installed apps
• Cosmetic wear and tear
• Accessories (chargers, earphones, etc.)`
      },
      {
        title: 'How to Claim Warranty',
        content: `1. Contact our support team at support@zobox.in
2. Provide order details and describe the issue
3. Share photos/videos of the issue
4. Our team will verify and arrange service/replacement
5. Service completed within 7-10 business days`
      },
      {
        title: 'Warranty Service Options',
        content: `Depending on the issue, we provide:
        
• Repair: Free repair at our service centers
• Replacement: Same or equivalent model (subject to availability)
• Refund: Store credit or bank refund (in rare cases)`
      },
      {
        title: 'Warranty Void Conditions',
        content: `Warranty becomes void if:
        
• Device is repaired by unauthorized service centers
• IMEI/Serial number is tampered
• Physical or water damage is detected
• Device is used for commercial purposes without disclosure`
      }
    ]
  },
  'shipping-policy': {
    title: 'Shipping Policy',
    lastUpdated: 'January 2024',
    sections: [
      {
        title: 'Delivery Timeline',
        content: `• Metro cities: 3-5 business days
• Tier 2 cities: 5-7 business days
• Remote areas: 7-10 business days

Timelines are estimates and may vary due to unforeseen circumstances.`
      },
      {
        title: 'Shipping Charges',
        content: `• Orders above ₹999: FREE delivery
• Orders below ₹999: ₹49 delivery charge
• Express delivery (where available): Additional ₹99`
      },
      {
        title: 'Delivery Partners',
        content: `We work with trusted delivery partners:
        
• Delhivery
• BlueDart
• DTDC
• Shadowfax

Delivery partner is assigned based on your location and availability.`
      },
      {
        title: 'Order Tracking',
        content: `Once your order is shipped:
        
• You'll receive SMS and email with tracking details
• Track your order on our website or delivery partner's app
• Contact support if tracking hasn't updated in 48 hours`
      },
      {
        title: 'Delivery Instructions',
        content: `• Ensure someone is available to receive the package
• Check package for damage before accepting
• Record unboxing video (recommended for returns/exchange)
• OTP verification may be required for delivery`
      },
      {
        title: 'Undelivered Packages',
        content: `If delivery fails:
        
• 2 more delivery attempts will be made
• Package held at local hub for 7 days
• After 7 days, package returns to our warehouse
• Refund processed minus shipping charges`
      }
    ]
  },
  'disclaimer': {
    title: 'Disclaimer',
    lastUpdated: 'January 2024',
    sections: [
      {
        title: 'General Disclaimer',
        content: `The information provided on the ZOBOX website is for general informational purposes only. We make every effort to ensure the accuracy and completeness of information, but we make no representations or warranties of any kind.`
      },
      {
        title: 'Product Information',
        content: `• Product images are for illustration purposes only
• Actual product may vary in appearance
• Specifications are subject to change without notice
• Prices may change without prior notice`
      },
      {
        title: 'Third-Party Links',
        content: `Our website may contain links to third-party websites. We are not responsible for the content, privacy policies, or practices of any third-party sites.`
      },
      {
        title: 'Limitation of Liability',
        content: `ZOBOX Protech Limited, its directors, employees, or agents shall not be liable for any direct, indirect, incidental, consequential, or punitive damages arising from:
        
• Use of our products or services
• Inability to use our services
• Unauthorized access to your data
• Any errors or omissions in content`
      },
      {
        title: 'Indemnification',
        content: `You agree to indemnify and hold harmless ZOBOX Protech Limited from any claims, losses, damages, liabilities, and expenses arising from your use of our services or violation of these terms.`
      }
    ]
  },
  'faqs': {
    title: 'Frequently Asked Questions',
    lastUpdated: 'January 2024',
    sections: [
      {
        title: 'What is a refurbished phone?',
        content: `A refurbished phone is a pre-owned device that has been professionally inspected, repaired if necessary, and restored to excellent working condition. At ZOBOX, every device undergoes 34+ quality checks before being sold.`
      },
      {
        title: 'What is the difference between ZOPRO and ZRP?',
        content: `ZOPRO products are our premium range with grades Excellent, Superb, and Good - they come with 6 months warranty. ZRP products include Fair and Okay grades with 7 days checking warranty - great for budget buyers.`
      },
      {
        title: 'Is it safe to buy refurbished phones?',
        content: `Yes, buying from ZOBOX is completely safe. We provide:
• Detailed quality grading
• Warranty coverage
• Easy returns within 7 days
• Secure payment options
• Verified device history (no stolen devices)`
      },
      {
        title: 'How do I track my order?',
        content: `After your order ships, you'll receive SMS and email with tracking details. You can also:
1. Login to your ZOBOX account
2. Go to 'My Orders'
3. Click on your order to see tracking status`
      },
      {
        title: 'What payment methods do you accept?',
        content: `We accept:
• UPI (GPay, PhonePe, Paytm, etc.)
• Credit/Debit Cards (Visa, Mastercard, RuPay)
• Net Banking
• Wallets (Paytm, MobiKwik)
• EMI options on select cards
• Cash on Delivery`
      },
      {
        title: 'Can I return a product?',
        content: `Yes, we offer 7-day returns. The product must be in original condition with all accessories. Initiate returns through your account or contact our support team.`
      },
      {
        title: 'How does the warranty work?',
        content: `ZOPRO products have 6 months warranty, ZRP has 7 days checking warranty. Warranty covers manufacturing defects and hardware issues. Physical damage, water damage, and unauthorized repairs are not covered.`
      },
      {
        title: 'Do you have physical stores?',
        content: `Yes! We have Zomart stores across multiple cities. Use our Store Locator to find the nearest store. You can also buy from our franchise partners - Zomart Plus, Zomart Pro, and Zomart Maxx.`
      },
      {
        title: 'How can I become a reseller?',
        content: `Download the ZOBIZ app from Play Store. Register as a reseller, get verified, and start buying at wholesale prices. You can also visit our nearest Zomart store for B2R purchases.`
      },
      {
        title: 'How do I contact customer support?',
        content: `You can reach us via:
• Phone: +91 98765 43210 (Mon-Sat, 10AM-7PM)
• Email: support@zobox.in
• WhatsApp: +91 98765 43210
• Live Chat: Available on website`
      }
    ]
  }
};

function ShopPolicy() {
  const { policyType } = useParams();
  const policy = policies[policyType] || policies['privacy'];

  return (
    <ShopLayout>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary to-blue-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">{policy.title}</h1>
          <p className="text-white/80">Last updated: {policy.lastUpdated}</p>
        </div>
      </section>

      {/* Policy Content */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="space-y-8">
            {policy.sections.map((section, index) => (
              <div key={index} className="border-b border-gray-200 pb-8 last:border-0">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">{section.title}</h2>
                <div className="text-gray-600 whitespace-pre-line">{section.content}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </ShopLayout>
  );
}

export default ShopPolicy;
